import { useCallback, useEffect, useRef, useState } from "react";

const VISITED_CHATS_SESSION_KEY = "zapi-chat-visited";

function readVisitedChats(): string[] {
  try {
    const raw = sessionStorage.getItem(VISITED_CHATS_SESSION_KEY);
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    return [];
  }
}

function markChatVisited(key: string) {
  try {
    const visited = readVisitedChats();
    if (visited.includes(key)) return;
    sessionStorage.setItem(VISITED_CHATS_SESSION_KEY, JSON.stringify([...visited, key]));
  } catch {
    // storage unavailable (private mode) — the dot just shows again next visit
  }
}

/**
 * Counts assistant messages that carry actual content, ignoring the empty
 * placeholder bubble added optimistically while a response streams in. Counting
 * the placeholder would let a close (which marks messages seen) capture the
 * pending answer, so finishing it wouldn't raise the count and no unread dot
 * would appear.
 */
export function countVisibleAssistantMessages(messages: { role: string; content: string }[]): number {
  return messages.filter((m) => m.role !== "user" && hasAssistantContent(m.content)).length;
}

function hasAssistantContent(content: string): boolean {
  if (!content) return false;
  try {
    const parsed = JSON.parse(content);
    if (parsed && typeof parsed === "object") {
      if (typeof parsed.content === "string") return parsed.content.trim().length > 0;
      return true;
    }
  } catch {
    // not JSON — treat as raw text below
  }
  return content.trim().length > 0;
}

type TChatUnreadIndicatorOptions = {
  /**
   * Gates the initial baseline so pre-existing/loading messages never count as
   * unread.
   */
  ready?: boolean;
  /**
   * Raises the dot on entrance for chats the user must notice every time the
   * page opens, independent of what the conversation holds. Read once on mount.
   */
  unreadOnMount?: boolean;
  /**
   * Raises the dot the first time this chat is reached in a browser session,
   * even with nothing new to read, so tablet/mobile users notice the collapsed
   * chat is there at all.
   */
  firstVisitKey?: string;
  /**
   * Re-baselines the seen count when the underlying conversation changes, so
   * switching to a longer conversation doesn't look like new messages.
   */
  conversationKey?: string;
};

/**
 * Tracks whether the assistant produced messages the user hasn't seen yet. Call
 * `markSeen` when the chat becomes visible, and `markUnread` for results that
 * reach the user outside the message list (e.g. a finished outline).
 */
export function useChatUnreadIndicator(assistantMessageCount: number, options: TChatUnreadIndicatorOptions = {}) {
  const { ready = true, unreadOnMount = false, firstVisitKey, conversationKey } = options;
  const [seenCount, setSeenCount] = useState<number | null>(null);
  const [isEntranceUnread, setIsEntranceUnread] = useState(unreadOnMount);
  const [isEventUnread, setIsEventUnread] = useState(false);
  const [isFirstVisit, setIsFirstVisit] = useState(false);
  const trackedFirstVisitKey = useRef<string | null>(null);

  useEffect(() => {
    setSeenCount(null);
    setIsEventUnread(false);
  }, [conversationKey]);

  useEffect(() => {
    if (ready && seenCount === null) {
      setSeenCount(assistantMessageCount);
    }
  }, [ready, seenCount, assistantMessageCount]);

  useEffect(() => {
    if (!firstVisitKey || trackedFirstVisitKey.current === firstVisitKey) return;
    trackedFirstVisitKey.current = firstVisitKey;
    setIsFirstVisit(!readVisitedChats().includes(firstVisitKey));
  }, [firstVisitKey]);

  const hasUnread =
    isEntranceUnread || isEventUnread || isFirstVisit || (seenCount !== null && assistantMessageCount > seenCount);

  const markSeen = useCallback(() => {
    setSeenCount(assistantMessageCount);
    setIsEntranceUnread(false);
    setIsEventUnread(false);
    setIsFirstVisit(false);
    if (firstVisitKey) markChatVisited(firstVisitKey);
  }, [assistantMessageCount, firstVisitKey]);

  const markUnread = useCallback(() => setIsEventUnread(true), []);

  return { hasUnread, markSeen, markUnread };
}
